import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, RotateCcw, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useFirma } from "../context/FirmaContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

export const ARCHIVIO_ROLES = ["super_admin", "admin", "responsabile", "coordinatore"];
const fmtTs = (ts) => { if (!ts) return ""; const d = new Date(ts); return isNaN(d) ? "" : d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); };

// Conferma + spostamento in archivio (al posto della cancellazione definitiva)
export function useConfermaArchivio() {
  const { signerName } = useFirma();
  const qc = useQueryClient();
  const [req, setReq] = useState(null);
  const [busy, setBusy] = useState(false);
  const ask = (body, testo, onDone) => setReq({ body, testo, onDone });
  const conferma = async () => {
    setBusy(true);
    try {
      await api.post("/infettive/archivio/elimina", { operatore: signerName, ...req.body });
      toast.success("Eliminato e spostato in archivio");
      qc.invalidateQueries({ queryKey: ["inf-archivio"] });
      req.onDone?.();
      setReq(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nell'eliminazione"); }
    setBusy(false);
  };
  const dialog = (
    <Dialog open={!!req} onOpenChange={(o) => { if (!o) setReq(null); }}>
      <DialogContent className="max-w-md" data-testid="archivia-conferma-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Trash2 size={18} className="text-red-600" /> Confermi l'eliminazione?</DialogTitle>
          <DialogDescription>Stai eliminando <b className="text-foreground">{req?.testo}</b>. Verrà spostato nell'archivio: un amministratore o un responsabile potrà ripristinarlo.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setReq(null)} data-testid="archivia-annulla">Annulla</Button>
          <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={conferma} disabled={busy} data-testid="archivia-conferma">Sì, elimina</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
  return { ask, dialog };
}

const VoceArchivio = ({ r, onRestore, busy }) => (
  <div className="rounded-lg border bg-white p-3 flex items-start gap-3" data-testid={`archivio-voce-${r.id}`}>
    <div className="min-w-0 flex-1 text-sm">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 bg-slate-100 text-slate-600">{r.tipo_label}</span>
        <b>{r.ospite_nome || "—"}</b>
      </div>
      {r.descrizione && <div className="mt-1 text-slate-700 break-words">{r.descrizione}</div>}
      <div className="mt-1 text-[11px] text-muted-foreground">
        {r.operatore ? <>Registrato da {r.operatore} · </> : null}Eliminato da <b>{r.eliminato_da || "—"}</b> il {fmtTs(r.eliminato_at)}
      </div>
    </div>
    <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={() => onRestore(r)} disabled={busy} data-testid={`archivio-ripristina-${r.id}`}><RotateCcw size={14} className="mr-1" /> Ripristina</Button>
  </div>
);

export function ArchivioEventiButton({ ospiteId }) {
  const { user, selectedStructure } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: list = [], isLoading } = useQuery({
    queryKey: ["inf-archivio", selectedStructure, ospiteId || ""],
    queryFn: async () => (await api.get("/infettive/archivio", { params: { structure_id: selectedStructure, ospite_id: ospiteId || undefined } })).data,
    enabled: open,
  });
  if (!ARCHIVIO_ROLES.includes(user?.role)) return null;
  const restore = async (r) => {
    setBusy(true);
    try {
      await api.post(`/infettive/archivio/${r.id}/ripristina`);
      toast.success("Ripristinato");
      qc.invalidateQueries();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nel ripristino"); }
    setBusy(false);
  };
  const t = q.trim().toLowerCase();
  const rows = list.filter((r) => !t || `${r.ospite_nome} ${r.descrizione} ${r.tipo_label} ${r.eliminato_da}`.toLowerCase().includes(t));
  return (
    <>
      <Button size="sm" variant="outline" className="h-9" onClick={() => setOpen(true)} data-testid={ospiteId ? "scheda-archivio-btn" : "inf-archivio-btn"}><Archive size={15} className="mr-1" /> Archivio</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="inf-archivio-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Archive size={18} /> Archivio eliminati</DialogTitle>
            <DialogDescription>{ospiteId ? "Elementi eliminati dagli eventi sanitari di questo ospite." : "Elementi eliminati dagli eventi sanitari."} Puoi ripristinarli dove si trovavano.</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca ospite, operatore, contenuto…" className="pl-8" data-testid="inf-archivio-search" />
          </div>
          {isLoading ? <div className="text-sm text-muted-foreground">Caricamento…</div>
            : rows.length === 0 ? <div className="text-sm text-muted-foreground text-center py-6" data-testid="inf-archivio-vuoto">Nessun elemento in archivio.</div>
            : <div className="space-y-2">{rows.map((r) => <VoceArchivio key={r.id} r={r} onRestore={restore} busy={busy} />)}</div>}
        </DialogContent>
      </Dialog>
    </>
  );
}
